import { useMemo } from "react";

/**
 * Custom hook to centralize loading and error states for OverviewPage
 * @param {Object} queries - Object containing all the query hooks
 * @returns {Object} Centralized loading and error states
 */
export const useOverviewStates = (queries) => {
  const {
    recentOrdersData,
    customerSatisfactionData,
    aiMetricsData,
    aiProductsData,
    aiAgentsData,
    aiPerformanceData,
  } = queries;

  // Only show initial loading state when ALL queries are loading and NONE have data yet
  const isLoading = useMemo(() => {
    const allLoading = [
      recentOrdersData.isLoading,
      customerSatisfactionData.isLoading,
      aiMetricsData.isLoading,
      aiProductsData.isLoading,
      aiAgentsData.isLoading,
      aiPerformanceData.isLoading,
    ].every(Boolean);

    const anyHasData = [
      recentOrdersData.data,
      customerSatisfactionData.data,
      aiMetricsData.data,
      aiProductsData.data,
      aiAgentsData.data,
      aiPerformanceData.data,
    ].some(Boolean);

    // Only show loading if all are loading AND no data is available yet
    return allLoading && !anyHasData;
  }, [
    recentOrdersData.isLoading,
    recentOrdersData.data,
    customerSatisfactionData.isLoading,
    customerSatisfactionData.data,
    aiMetricsData.isLoading,
    aiMetricsData.data,
    aiProductsData.isLoading,
    aiProductsData.data,
    aiAgentsData.isLoading,
    aiAgentsData.data,
    aiPerformanceData.isLoading,
    aiPerformanceData.data,
  ]);

  const error = null;

  // Check if any data is available (for partial loading states)
  const hasAnyData = useMemo(() => {
    return [
      recentOrdersData.data,
      customerSatisfactionData.data,
      aiMetricsData.data,
      aiProductsData.data,
      aiAgentsData.data,
      aiPerformanceData.data,
    ].some(Boolean);
  }, [
    recentOrdersData.data,
    customerSatisfactionData.data,
    aiMetricsData.data,
    aiProductsData.data,
    aiAgentsData.data,
    aiPerformanceData.data,
  ]);

  return {
    isLoading,
    error,
    hasAnyData,
    // Individual states for specific use cases
    individualStates: {
      recentOrders: recentOrdersData,
      customerSatisfaction: customerSatisfactionData,
      aiMetrics: aiMetricsData,
      aiProducts: aiProductsData,
      aiAgents: aiAgentsData,
      aiPerformance: aiPerformanceData,
    },
  };
};
